import {
    IApiAxiosError,
    IClauseChecklistImportPreprocessResponse,
    IClauseChecklistImportRequest,
    importClauseChecklist,
    preprocessClauseChecklistImport,
} from "@/api";
import { IModalAction, Modal } from "@/components/shared/Modal";
import {
    Alert,
    AlertTitle,
    Box,
    Step,
    StepLabel,
    Stepper,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { DetailsStep } from "./DetailsStep";
import { ReviewStep } from "./ReviewStep";
import { UploadStep } from "./UploadStep";
import { IImportFormData } from "./types";

enum ImportSteps {
    Upload,
    Details,
    Review,
}

interface ImportChecklistDialogProps {
    onOuterMenuClose?: () => void;
    open: boolean;
    onClose: () => void;
}

export const ImportChecklistDialog = ({
    open,
    onClose,
}: ImportChecklistDialogProps) => {
    const [step, setStep] = useState<ImportSteps>(ImportSteps.Upload);
    const [formData, setFormData] = useState<IImportFormData>({
        name: "",
        description: "",
        organizationId: undefined,
        file: undefined,
        objectName: undefined,
    });
    const [reviewStats, setReviewStats] =
        useState<IClauseChecklistImportPreprocessResponse>();
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const preprocessMutation = useMutation({
        mutationFn: (importIn: IClauseChecklistImportRequest) =>
            preprocessClauseChecklistImport(importIn),
        onSuccess: (data) => {
            setError(null);
            setReviewStats(data);
            setStep(ImportSteps.Review);
        },
        onError: (error) => {
            const err = error as IApiAxiosError;
            setError(
                err.response?.data.detail || "An unexpected error occurred"
            );
        },
    });

    const importMutation = useMutation({
        mutationFn: (importIn: IClauseChecklistImportRequest) =>
            importClauseChecklist(importIn),
        onSuccess: (newChecklist) => {
            navigate(`${newChecklist.id}/details`);
        },
        onError: (error) => {
            const err = error as IApiAxiosError;
            setError(err.response?.data.detail || "Import failed unexpectedly");
        },
    });

    const handleClose = useCallback(() => {
        // Only allow closing if mutations are not pending
        if (preprocessMutation.isPending || importMutation.isPending) {
            return;
        }

        // Reset mutations before closing
        preprocessMutation.reset();
        importMutation.reset();

        onClose();
        // Reset form state when dialog closes
        setStep(ImportSteps.Upload);
        setFormData({
            name: "",
            description: "",
            organizationId: undefined,
            file: undefined,
            objectName: undefined,
        });
        setReviewStats(undefined);
        setError(null);
    }, [preprocessMutation, importMutation, onClose]);

    const buildImportPayload = (): IClauseChecklistImportRequest | null => {
        if (!formData.organizationId || !formData.objectName) return null;
        return {
            name: formData.name,
            description: formData.description,
            organization_id: formData.organizationId,
            object_name: formData.objectName,
        };
    };

    const handlePreprocess = () => {
        const payload = buildImportPayload();
        if (payload) preprocessMutation.mutate(payload);
    };

    const handleImport = () => {
        const payload = buildImportPayload();
        if (payload) importMutation.mutate(payload);
    };

    const renderStepContent = () => {
        switch (step) {
            case ImportSteps.Upload:
                return (
                    <UploadStep
                        formData={formData}
                        onFormDataChange={setFormData}
                    />
                );
            case ImportSteps.Details:
                return (
                    <DetailsStep
                        formData={formData}
                        onFormDataChange={setFormData}
                    />
                );
            case ImportSteps.Review:
                return reviewStats ? (
                    <ReviewStep reviewStats={reviewStats} formData={formData} />
                ) : null;
            default:
                return null;
        }
    };

    // Form validation helpers
    const isDetailsStepValid = () => {
        return (
            formData.name.trim() !== "" &&
            formData.description.trim() !== "" &&
            formData.organizationId
        );
    };

    const renderActions = (): IModalAction[] | undefined => {
        switch (step) {
            case ImportSteps.Upload:
                return [
                    {
                        label: "Next",
                        onClick: () => setStep(ImportSteps.Details),
                        variant: "contained",
                        disabled: !formData.objectName,
                    },
                ];
            case ImportSteps.Details:
                return [
                    {
                        label: "Back",
                        onClick: () => setStep(ImportSteps.Upload),
                        variant: "outlined",
                    },
                    {
                        label: "Next",
                        onClick: () => handlePreprocess(),
                        variant: "contained",
                        disabled:
                            !isDetailsStepValid() ||
                            preprocessMutation.isPending,
                    },
                ];
            case ImportSteps.Review:
                return [
                    {
                        label: "Back",
                        onClick: () => setStep(ImportSteps.Details),
                        variant: "outlined",
                    },
                    {
                        label: "Import",
                        onClick: () => handleImport(),
                        disabled: importMutation.isPending,
                        variant: "contained",
                    },
                ];
        }
    };

    const getFooterText = () => {
        switch (step) {
            case ImportSteps.Upload:
                return "Upload an Excel file to continue";
            case ImportSteps.Details:
                return "Provide checklist details to continue";
            case ImportSteps.Review:
                return "Please review the details before confirming the import";
            default:
                return undefined;
        }
    };

    const isLoading = preprocessMutation.isPending || importMutation.isPending;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Import Checklist"
            actions={renderActions()}
            maxWidth="sm"
            showCancel={false}
            isLoading={isLoading}
            footerText={getFooterText()}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                }}
            >
                <Stepper activeStep={step} alternativeLabel>
                    {Object.values(ImportSteps)
                        .filter((value) => typeof value === "string")
                        .map((label, index) => (
                            <Step key={index}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                </Stepper>

                {error && (
                    <Alert severity="error">
                        <AlertTitle>Error</AlertTitle>
                        {error}
                    </Alert>
                )}

                {renderStepContent()}
            </Box>
        </Modal>
    );
};
